/**
 * Tower textures — the collection as data a shader can draw, not as baked frames.
 *
 * `tools/atlas.ts` bakes one still per token into sixteen 2048px pages: 256 MB of VRAM for
 * 4096 parcels, and no animation, because twelve frames each would be sixteen times that.
 * `tools/animbudget.ts` measured the alternative at 8.67 MB. So the animated tower ships *the
 * data the art is made of* and draws a frame in GLSL. `emit/glsl.ts` is that shader, mirroring
 * `art/glitch.ts` and the shipped player; this file is the memory layout the two agree on, and
 * `tools/towerdata.ts` is what writes it.
 *
 * Four data textures plus one glyph atlas, and every address in them is arithmetic on the token
 * id — no lookup table crosses the wire:
 *
 *   cells      R8UI 2048x2048     one 32x32 block per token, the composed still
 *   palettes   RGBA8 16x4096      one row per token, one texel per colour slot, raw bytes
 *   schedules  RGBA32UI 1024x192  four texels per (token, frame): header, then band words
 *   blocks     RGBA8 512x512      8x8 mean colours per token, the far-LOD fallback
 *   glyphs     R8 64x64x108       coverage per (glyph set, glyph index), mipmapped
 *
 * Three layout decisions carry the design.
 *
 * **A token's cells are a square block, not a run.** The obvious packing is `id * 1024` bytes
 * of a linear array, and it wastes the texture cache: neighbouring pixels of a parcel read
 * neighbouring *rows* of the grid, which in a linear packing are 32 bytes apart in one 2048-wide
 * row — the same cache line for two rows and never for four. A 32x32 block puts the whole
 * 3x3 neighbourhood a fragment quad can touch inside a few lines. It costs the baker 32 row
 * copies per token instead of one, which is nothing.
 *
 * **The palette is raw sRGB bytes, and must stay that way.** Canvas 2D composites the player's
 * frames in sRGB byte space — `fillStyle` is a hex string, `globalAlpha` blends the bytes — and
 * so does resvg. A palette texture tagged `SRGBColorSpace` would be uploaded as `SRGB8_ALPHA8`
 * and `texture()` would hand the shader *linear* values, which is a different image. The shader
 * therefore reads bytes, blends bytes, and writes bytes, which is bit-for-bit what the player
 * does. See `emit/glsl.ts`.
 *
 * **The schedule is dense.** Only 2.45 frames of 12 are hot on average, so four fifths of this
 * texture is zeros — 3 MB of VRAM to avoid an indirection table, and 137 KB on the wire after gzip
 * (measured by `tools/towerdata.ts`, which weighs every file both ways) because zeros compress.
 * The alternative, an offset per (token, frame) into a packed list, costs a dependent fetch on
 * every fragment of every animated parcel. Zeros are cheaper.
 */

import { CELL_COUNT, GLITCH_FRAMES, GRID_COLS, GRID_ROWS, MAX_SUPPLY } from "../constants.js";
import { GLYPH_BLANK, GLYPH_COUNT, GLYPH_FILL, cellGlyph, cellIndex, cellSlot } from "../art/cell.js";
import type { Grid } from "../art/cell.js";
import { bandRows } from "../art/glitch.js";
import type { Frame, Schedule, Slice } from "../art/glitch.js";
import { SLOT_BG, SLOT_COUNT } from "../art/palette.js";
import { GLITCH_LEVELS } from "../data/glitch.js";
import { GLYPH_SETS } from "../data/glyphs.js";
import { iabs, idiv } from "../engine/int.js";

/** Cell texture side. `2048 * 2048` is exactly `MAX_SUPPLY * CELL_COUNT`, with nothing spare. */
export const CELLS_TEX_PX = 2048;
/** Token blocks per texture row. */
export const CELL_BLOCKS_PER_ROW = CELLS_TEX_PX / GRID_COLS;

/** Palette texture: one row per token, one texel per slot. */
export const PAL_TEX_W = SLOT_COUNT;
export const PAL_TEX_H = MAX_SUPPLY;

/**
 * Schedule texture: `RGBA32UI`, four texels per (token, frame).
 *
 * Texel 0 is the header — `flags`, `chroma`, `bands`, `corruptSeed`. Texels 1 and 2 hold up to
 * seven band words, one per band, packed by `packBandWord`. Texel 3 is reserved and zero, which
 * is what makes the record stride a power of two: the address is two shifts and a mask.
 *
 * A fragment reads two of the four — the header, and the one texel holding its row's band — so
 * the padding is not paid for in bandwidth.
 */
export const SCHED_TEXELS_PER_FRAME = 4;
export const SCHED_WORDS_PER_FRAME = SCHED_TEXELS_PER_FRAME * 4;
export const SCHED_TEX_W = 1024;
export const SCHED_TEX_H = (MAX_SUPPLY * GLITCH_FRAMES * SCHED_TEXELS_PER_FRAME) / SCHED_TEX_W;
/** Words before the first band word: the whole of texel 0. `emit/glsl.ts` skips the same four. */
export const SCHED_HEADER_WORDS = 4;
/**
 * Bands the record holds. `data/glitch.ts` authors seven at Collapse and `validateGlitch` caps
 * any future level at `GRID_ROWS / 4` — which is eight, so this is a real limit and
 * `writeSchedule` throws rather than truncating a frame silently.
 */
export const SCHED_MAX_BANDS = 7;

/** Header word 0, bit flags. `hot` is not derivable from the rest: a hot frame can be all zeros. */
export const SCHED_FLAG_HOT = 1;
export const SCHED_FLAG_FLICKER = 2;

/**
 * Far-LOD block texture: the still reduced to mean colours, `BLOCK_CELLS` cells to a side.
 *
 * Four cells to a block is the granularity that matters, because it is where per-cell sampling
 * stops working: a parcel four pixels wide has one fragment per four cells, and point-sampling
 * the cell grid there is the shimmer this texture exists to remove.
 *
 * The mip chain does the rest of the ladder for free. A token's tile is 8x8 texels, so levels 1,
 * 2 and 3 are 8-cell, 16-cell and whole-token means and *none* of them crosses into a neighbour
 * — which is why `BLOCK_TOP_LEVEL` is the highest level the shader may sample. Level 4 would
 * average two tokens together.
 */
export const BLOCK_CELLS = 4;
export const BLOCKS_PER_SIDE = GRID_COLS / BLOCK_CELLS;
export const BLOCK_TEX_PX = CELL_BLOCKS_PER_ROW * BLOCKS_PER_SIDE;
export const BLOCK_TOP_LEVEL = Math.log2(BLOCKS_PER_SIDE);

/**
 * Glyph atlas: one square layer per (glyph set, glyph index), coverage in [0, 255].
 *
 * Square, because a cell *reads* square. The engine lays glyphs out in text space — a
 * `CELL_W` by `CELL_H` portrait box — and hands the viewer a square with
 * `preserveAspectRatio="none"`, so the mark the eye sees is that box stretched by 1.9 in x. A
 * layer bakes the stretch in, and the shader's cell-local uv then indexes it directly.
 *
 * An *array* texture rather than one big atlas, because this is minified hard and the whole
 * point is to let the hardware do it. Mipmapping a flat atlas bleeds neighbouring tiles into
 * each other at the top levels; array layers are independent, so level 6 of a layer is exactly
 * that glyph's mean coverage over the cell — which is the value a parcel a few pixels wide
 * needs, obtained with no extra table and no branch.
 */
export const GLYPH_TILE_PX = 64;
export const GLYPH_LAYERS = GLYPH_SETS.length * GLYPH_COUNT;

/** Byte counts, so the baker and the report cannot disagree with the layout. */
export const CELLS_BYTES = CELLS_TEX_PX * CELLS_TEX_PX;
export const PAL_BYTES = PAL_TEX_W * PAL_TEX_H * 4;
export const SCHED_BYTES = MAX_SUPPLY * GLITCH_FRAMES * SCHED_WORDS_PER_FRAME * 4;
export const BLOCK_BYTES = BLOCK_TEX_PX * BLOCK_TEX_PX * 4;
export const GLYPH_BYTES = GLYPH_LAYERS * GLYPH_TILE_PX * GLYPH_TILE_PX;

/**
 * Byte offset of cell `(x, y)` of token `id`.
 *
 * The token's 32x32 block sits at block column `id % 64`, block row `id / 64`, so ids ascend
 * left to right and top to bottom of the texture. Nothing else in the pipeline depends on that
 * order — the shader computes it — but it means a dump of the texture is a readable contact
 * sheet, which is worth having when a parcel comes out wrong.
 */
export function cellsOffset(id: number, x: number, y: number): number {
  const bx = id % CELL_BLOCKS_PER_ROW;
  const by = idiv(id, CELL_BLOCKS_PER_ROW);
  return (by * GRID_ROWS + y) * CELLS_TEX_PX + bx * GRID_COLS + x;
}

/** One token's composed still, row by row into its block. */
export function writeCells(dst: Uint8Array, id: number, grid: Grid): void {
  if (grid.length !== CELL_COUNT) throw new Error(`token ${id}: grid is ${grid.length} cells`);
  for (let y = 0; y < GRID_ROWS; y++) {
    dst.set(grid.subarray(y * GRID_COLS, (y + 1) * GRID_COLS), cellsOffset(id, 0, y));
  }
}

/** Byte offset of slot `slot` of token `id`. */
export function paletteOffset(id: number, slot: number): number {
  return (id * SLOT_COUNT + slot) * 4;
}

/**
 * One token's resolved slots as raw sRGB bytes, alpha 255.
 *
 * `resolveSlots` has already applied the tint, so this is the table the player holds as sixteen
 * hex strings — the same numbers, in the same order, with no colour management in between.
 */
export function writePalette(dst: Uint8Array, id: number, slots: readonly number[]): void {
  if (slots.length !== SLOT_COUNT) throw new Error(`token ${id}: ${slots.length} slots`);
  for (let s = 0; s < SLOT_COUNT; s++) {
    const c = slots[s] as number;
    if (c < 0 || c > 0xffffff) throw new Error(`token ${id} slot ${s}: ${c} is not a colour`);
    const o = paletteOffset(id, s);
    dst[o] = (c >> 16) & 0xff;
    dst[o + 1] = (c >> 8) & 0xff;
    dst[o + 2] = c & 0xff;
    dst[o + 3] = 255;
  }
}

/**
 * Which band row `y` belongs to, when the grid is cut into `bands`.
 *
 * `bandRows` is the forward map — band `b` starts at `floor(b * GRID_ROWS / bands)` — and this is
 * its exact inverse: the last band to start at or before row `y` is
 * `floor(((y + 1) * bands - 1) / GRID_ROWS)`. `validateTowerTex` checks it against `bandRows` for
 * every band count a level may author and every row of the grid, because the shader trusts it:
 * addressing the band directly is one texel fetch per fragment where scanning seven band words
 * for the one that contains `y` would be two, on every fragment of every animated parcel.
 */
export function bandOfRow(y: number, bands: number): number {
  return idiv((y + 1) * bands - 1, GRID_ROWS);
}

/** Largest displacement a band word can hold. `validateGlitch` caps `maxSlice` at eight. */
export const BAND_MAG_MAX = 15;

/**
 * Where the band word's fields sit. `emit/glsl.ts` unpacks the same bits on the GPU, so the
 * positions are constants rather than literals in two places — five rows for `y0`, five for
 * `y1`, four for the magnitude, one for the sign, fifteen bits in total.
 */
export const BAND_Y_BITS = 5;
export const BAND_Y1_SHIFT = BAND_Y_BITS;
export const BAND_MAG_SHIFT = BAND_Y_BITS * 2;
export const BAND_SIGN_BIT = 1 << 14;

/**
 * One band as a single word: rows in the low ten bits, then magnitude and sign.
 *
 * `y0` and `y1` are derivable from the band count, and are stored anyway. It costs nothing — they
 * share the word the shift needs — and it keeps the record self-describing, so a gate can read
 * the texture back and check it against the schedule without re-deriving Bresenham boundaries in
 * a second place.
 */
export function packBandWord(y0: number, y1: number, shift: number): number {
  const mag = iabs(shift);
  if (y0 < 0 || y1 >= GRID_ROWS || y1 < y0) throw new Error(`band rows ${y0}..${y1} are not a band`);
  if (mag < 1 || mag > BAND_MAG_MAX) throw new Error(`a shift of ${shift} does not fit a band word`);
  return y0 | (y1 << BAND_Y1_SHIFT) | (mag << BAND_MAG_SHIFT) | (shift < 0 ? BAND_SIGN_BIT : 0);
}

const BAND_Y_MASK = (1 << BAND_Y_BITS) - 1;

export function bandWordRows(word: number): { readonly y0: number; readonly y1: number } {
  return { y0: word & BAND_Y_MASK, y1: (word >> BAND_Y1_SHIFT) & BAND_Y_MASK };
}

export function bandWordShift(word: number): number {
  const mag = (word >> BAND_MAG_SHIFT) & BAND_MAG_MAX;
  return (word & BAND_SIGN_BIT) !== 0 ? -mag : mag;
}

/** First word of the (token, frame) record. */
export function scheduleWord(id: number, frame: number): number {
  return (id * GLITCH_FRAMES + frame) * SCHED_WORDS_PER_FRAME;
}

/**
 * One token's twelve frames.
 *
 * A clean frame writes nothing: the record is already zero, `SCHED_FLAG_HOT` is clear, and the
 * shader reads that as "the still, untouched" — which is exactly what `applyFrame` returns for a
 * frame that is not hot. `hot` is a flag rather than `bands > 0` because the two are not the same
 * claim, and conflating them would make a future zero-band level render as a clean frame.
 */
export function writeSchedule(dst: Uint32Array, id: number, schedule: Schedule): void {
  if (schedule.frames.length !== GLITCH_FRAMES) {
    throw new Error(`token ${id}: ${schedule.frames.length} frames`);
  }
  for (let f = 0; f < GLITCH_FRAMES; f++) {
    const frame = schedule.frames[f] as Frame;
    if (!frame.hot) continue;
    if (frame.slices.length < 1 || frame.slices.length > SCHED_MAX_BANDS) {
      throw new Error(
        `token ${id} frame ${f} has ${frame.slices.length} bands, the record holds`
        + ` 1..${SCHED_MAX_BANDS}`,
      );
    }
    const base = scheduleWord(id, f);
    dst[base] = SCHED_FLAG_HOT | (frame.flicker ? SCHED_FLAG_FLICKER : 0);
    dst[base + 1] = frame.chroma;
    dst[base + 2] = frame.slices.length;
    dst[base + 3] = frame.corruptSeed >>> 0;
    for (let b = 0; b < frame.slices.length; b++) {
      const slice = frame.slices[b] as Slice;
      dst[base + SCHED_HEADER_WORDS + b] = packBandWord(slice.y0, slice.y1, slice.shift);
    }
  }
}

/** Byte offset of block `(bx, by)` of token `id`. */
export function blockOffset(id: number, bx: number, by: number): number {
  const tx = id % CELL_BLOCKS_PER_ROW;
  const ty = idiv(id, CELL_BLOCKS_PER_ROW);
  return ((ty * BLOCKS_PER_SIDE + by) * BLOCK_TEX_PX + tx * BLOCKS_PER_SIDE + bx) * 4;
}

/**
 * The still reduced to one mean colour per block.
 *
 * A cell in the main pass is its slot colour painted over the background at the glyph's coverage,
 * so that is what is averaged: `cover` is the glyph atlas's *measured* mean coverage for each
 * glyph index of this token's set, which is why the far LOD needs no separate table and cannot
 * disagree with the near one about how much ink a mark puts down.
 *
 * Averaged in sRGB byte space, on purpose. The player composites in byte space, the GPU reduces
 * this texture's own mip chain in byte space, and the stills atlas is downscaled in byte space; a
 * physically-correct average here would be the one value in the pipeline computed differently,
 * and the LOD cross-fade would show it as a step in brightness.
 */
export function writeBlocks(
  dst: Uint8Array,
  id: number,
  grid: Grid,
  slots: readonly number[],
  cover: readonly number[],
): void {
  if (cover.length !== GLYPH_COUNT) throw new Error(`the coverage table is ${cover.length} long`);
  if (cover[GLYPH_BLANK] !== 0) throw new Error("the blank glyph must have no coverage");
  if (cover[GLYPH_FILL] !== 1) throw new Error("the fill glyph must cover its whole cell");
  const bg = slots[SLOT_BG] as number;
  const per = BLOCK_CELLS * BLOCK_CELLS;
  for (let by = 0; by < BLOCKS_PER_SIDE; by++) {
    for (let bx = 0; bx < BLOCKS_PER_SIDE; bx++) {
      let r = 0;
      let g = 0;
      let b = 0;
      for (let cy = by * BLOCK_CELLS; cy < (by + 1) * BLOCK_CELLS; cy++) {
        for (let cx = bx * BLOCK_CELLS; cx < (bx + 1) * BLOCK_CELLS; cx++) {
          const cell = grid[cellIndex(cx, cy)] as number;
          const ink = slots[cellSlot(cell)] as number;
          const k = cover[cellGlyph(cell)] as number;
          r += ((bg >> 16) & 0xff) + (((ink >> 16) & 0xff) - ((bg >> 16) & 0xff)) * k;
          g += ((bg >> 8) & 0xff) + (((ink >> 8) & 0xff) - ((bg >> 8) & 0xff)) * k;
          b += (bg & 0xff) + ((ink & 0xff) - (bg & 0xff)) * k;
        }
      }
      const o = blockOffset(id, bx, by);
      dst[o] = Math.round(r / per);
      dst[o + 1] = Math.round(g / per);
      dst[o + 2] = Math.round(b / per);
      dst[o + 3] = 255;
    }
  }
}

/**
 * A token's mean colour, read back off its blocks.
 *
 * Every block covers the same number of cells, so the mean of the means is the mean of the grid
 * exactly. This is what `BLOCK_TOP_LEVEL` of the block texture's mip chain should hold, and
 * `apps/tower/shadercheck.ts` measures the GPU's answer against it rather than assuming they
 * agree about rounding.
 */
export function tokenColor(blocks: Uint8Array, id: number): number {
  let r = 0;
  let g = 0;
  let b = 0;
  for (let by = 0; by < BLOCKS_PER_SIDE; by++) {
    for (let bx = 0; bx < BLOCKS_PER_SIDE; bx++) {
      const o = blockOffset(id, bx, by);
      r += blocks[o] as number;
      g += blocks[o + 1] as number;
      b += blocks[o + 2] as number;
    }
  }
  const per = BLOCKS_PER_SIDE * BLOCKS_PER_SIDE;
  return (Math.round(r / per) << 16) | (Math.round(g / per) << 8) | Math.round(b / per);
}

/**
 * Structural guard invoked by tests.
 *
 * Everything the shader's addressing rests on, checked exhaustively rather than sampled: the
 * texture sizes are exactly the collection, each offset function is injective over its whole
 * domain, `bandOfRow` really is the inverse of `bandRows`, and a band word round-trips for every
 * band and displacement any authored level can produce.
 */
export function validateTowerTex(): void {
  if (CELLS_BYTES !== MAX_SUPPLY * CELL_COUNT) {
    throw new Error(`the cell texture holds ${CELLS_BYTES} bytes, the collection needs ${MAX_SUPPLY * CELL_COUNT}`);
  }
  if (CELLS_TEX_PX % GRID_COLS !== 0 || CELLS_TEX_PX % GRID_ROWS !== 0) {
    throw new Error(`a ${CELLS_TEX_PX}px cell texture does not tile with ${GRID_COLS}x${GRID_ROWS} blocks`);
  }
  if (PAL_BYTES !== MAX_SUPPLY * SLOT_COUNT * 4) throw new Error("the palette texture is the wrong size");
  if (!Number.isInteger(SCHED_TEX_H)) throw new Error(`the schedule texture is ${SCHED_TEX_H} rows tall`);
  if (SCHED_TEX_W % SCHED_TEXELS_PER_FRAME !== 0) {
    throw new Error("a schedule record straddles two rows of the texture");
  }
  if (SCHED_HEADER_WORDS + SCHED_MAX_BANDS > SCHED_WORDS_PER_FRAME) {
    throw new Error(`a header and ${SCHED_MAX_BANDS} bands do not fit ${SCHED_WORDS_PER_FRAME} words`);
  }
  for (const level of GLITCH_LEVELS) {
    if (level.sliceBands > SCHED_MAX_BANDS) {
      throw new Error(`${level.name} authors ${level.sliceBands} bands, the record holds ${SCHED_MAX_BANDS}`);
    }
    if (level.maxSlice > BAND_MAG_MAX) {
      throw new Error(`${level.name} displaces ${level.maxSlice} cells, a band word holds ${BAND_MAG_MAX}`);
    }
  }
  if (BLOCK_BYTES !== MAX_SUPPLY * BLOCKS_PER_SIDE * BLOCKS_PER_SIDE * 4) {
    throw new Error("the block texture is the wrong size");
  }
  if (!Number.isInteger(BLOCK_TOP_LEVEL)) throw new Error(`${BLOCKS_PER_SIDE} blocks a side is not a power of two`);
  if (GLYPH_BYTES !== GLYPH_SETS.length * GLYPH_COUNT * GLYPH_TILE_PX * GLYPH_TILE_PX) {
    throw new Error("the glyph atlas is the wrong size");
  }

  validateTowerAddressing();
  validateTowerBands();
}

/**
 * Every offset function is injective and lands inside its texture.
 *
 * Split out of `validateTowerTex` for length. The cell sweep is four million cells and a four
 * megabyte scratch array, which is a second of a test run and the only way to *prove* a block
 * layout does not overlap — an off-by-one in `cellsOffset` would paint one token over another
 * and look like a compose bug in the browser, which is a very expensive place to find it.
 */
function validateTowerAddressing(): void {
  const cells = new Uint8Array(CELLS_BYTES);
  for (let id = 0; id < MAX_SUPPLY; id++) {
    for (let y = 0; y < GRID_ROWS; y++) {
      for (let x = 0; x < GRID_COLS; x++) {
        const o = cellsOffset(id, x, y);
        if (o < 0 || o >= CELLS_BYTES) throw new Error(`token ${id} cell ${x},${y} is at byte ${o}`);
        if (cells[o] === 1) throw new Error(`two cells share byte ${o} of the cell texture`);
        cells[o] = 1;
      }
    }
  }

  const pal = new Uint8Array(PAL_BYTES / 4);
  for (let id = 0; id < MAX_SUPPLY; id++) {
    for (let s = 0; s < SLOT_COUNT; s++) {
      const o = paletteOffset(id, s);
      if (o % 4 !== 0 || o < 0 || o + 3 >= PAL_BYTES) throw new Error(`token ${id} slot ${s} is at byte ${o}`);
      if (pal[o / 4] === 1) throw new Error(`two slots share texel ${o / 4} of the palette texture`);
      pal[o / 4] = 1;
    }
  }

  const blocks = new Uint8Array(BLOCK_BYTES / 4);
  for (let id = 0; id < MAX_SUPPLY; id++) {
    for (let by = 0; by < BLOCKS_PER_SIDE; by++) {
      for (let bx = 0; bx < BLOCKS_PER_SIDE; bx++) {
        const o = blockOffset(id, bx, by);
        if (o % 4 !== 0 || o < 0 || o + 3 >= BLOCK_BYTES) throw new Error(`token ${id} block ${bx},${by} at ${o}`);
        if (blocks[o / 4] === 1) throw new Error(`two blocks share texel ${o / 4}`);
        blocks[o / 4] = 1;
      }
    }
  }

  const words = SCHED_BYTES / 4;
  const sched = new Uint8Array(words);
  for (let id = 0; id < MAX_SUPPLY; id++) {
    for (let f = 0; f < GLITCH_FRAMES; f++) {
      const base = scheduleWord(id, f);
      if (base % SCHED_WORDS_PER_FRAME !== 0) throw new Error(`token ${id} frame ${f} is unaligned`);
      if (base < 0 || base + SCHED_WORDS_PER_FRAME > words) throw new Error(`token ${id} frame ${f} at word ${base}`);
      if (sched[base] === 1) throw new Error(`two frames share word ${base} of the schedule texture`);
      sched[base] = 1;
      // The record must not cross a row, or the shader's two fetches would land on two lines.
      const texel = base / 4;
      if (texel % SCHED_TEX_W > SCHED_TEX_W - SCHED_TEXELS_PER_FRAME) {
        throw new Error(`token ${id} frame ${f} straddles a texture row`);
      }
    }
  }
}

/**
 * `bandOfRow` inverts `bandRows`, and a band word round-trips.
 *
 * Exhaustive over every band count a level may author — `validateGlitch` allows up to
 * `GRID_ROWS / 4` — every row of the grid, and every displacement a band word claims to hold.
 * This is the one piece of arithmetic the shader does *differently* from the engine rather than
 * identically to it, so it is checked against the engine here instead of being trusted.
 */
function validateTowerBands(): void {
  for (let bands = 1; bands * 4 <= GRID_ROWS; bands++) {
    for (let y = 0; y < GRID_ROWS; y++) {
      const band = bandOfRow(y, bands);
      if (band < 0 || band >= bands) throw new Error(`${bands} bands: row ${y} resolved to band ${band}`);
      const rows = bandRows(bands, band);
      if (y < rows.y0 || y > rows.y1) {
        throw new Error(`${bands} bands: row ${y} resolved to band ${band}, which is rows ${rows.y0}..${rows.y1}`);
      }
    }
    for (let band = 0; band < bands; band++) {
      const rows = bandRows(bands, band);
      for (let mag = 1; mag <= BAND_MAG_MAX; mag++) {
        for (const shift of [mag, -mag]) {
          const word = packBandWord(rows.y0, rows.y1, shift);
          if (word < 0 || word > 0x7fff) throw new Error(`band word ${word} does not fit fifteen bits`);
          const back = bandWordRows(word);
          if (back.y0 !== rows.y0 || back.y1 !== rows.y1) {
            throw new Error(`rows ${rows.y0}..${rows.y1} came back as ${back.y0}..${back.y1}`);
          }
          if (bandWordShift(word) !== shift) {
            throw new Error(`a shift of ${shift} came back as ${bandWordShift(word)}`);
          }
        }
      }
    }
  }
}
